import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import { HrService } from '@app/hr/hr.service';
import { LoginService } from '@app/common/login.service';
import { AlertService } from '@app/shared/alert.service';
import { hrIsHrManager } from '@app/shared/_models/hr';

/**
 * Écrans réservés au porteur de l'HABILITATION Responsable RH (c_values 31, id 1) : fiches du
 * personnel, feuilles d'heures, rapport de paie, paramétrage RH.
 *
 * Depuis la scission du 2026-08-03, l'habilitation Admin (0) NE SUFFIT PLUS : administrer
 * l'application n'ouvre pas les salaires. Un administrateur qui a besoin de ces écrans se donne
 * le 1 depuis Personnel & rôles.
 *
 * Ce garde est du CONFORT, pas de la sécurité : il évite d'ouvrir une page qui n'afficherait que
 * des blancs, et il dit POURQUOI. La donnée, elle, est retenue par le serveur — `/hr/employees`
 * ne renvoie pas les salaires, `/hr/documents` pas les contrats des autres, et toute écriture RH
 * est refusée en 403. Contourner le garde (URL tapée à la main, cache Angular) ne donne donc
 * accès à rien.
 *
 * Les habilitations sont lues dans la LISTE des users (la base) plutôt que dans le JWT : un token
 * émis avant la scission ne porte pas encore le champ `habilitations` et fermerait la porte à un
 * responsable RH légitime jusqu'à sa reconnexion. Si la liste est inaccessible, on retombe sur les
 * habilitations du token — la seule chose disponible.
 */
export const hrManagerGuard: CanActivateFn = (_route, state): Observable<boolean> => {
	const loginService = inject(LoginService);
	const hrService = inject(HrService);
	const alertService = inject(AlertService);
	const router = inject(Router);

	if (!loginService.loggeduser) {
		router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
		return of(false);
	}

	// Liste CACHÉE par HrService (TTL 60 s, invalidée à chaque écriture) : le garde est posé sur
	// toutes les routes réservées, sans cache chaque navigation refaisait le même GET /users.
	return hrService.getUsers$().pipe(
		take(1),
		catchError(() => of([])),
		map(users => {
			if (hrIsHrManager(loginService.loggeduser, users)) return true;
			alertService.error('Réservé au responsable des ressources humaines.');
			router.navigate(['/hr/planning']);
			return false;
		}),
	);
};
